import { useMemo } from "react";
import { motion } from "framer-motion";
import { RowsPhotoAlbum } from "react-photo-album";
import "react-photo-album/rows.css";
import { Heart, Download } from "lucide-react";
import ChapterDivider from "../shared/ChapterDivider";
import FooterSignature from "../shared/FooterSignature";
import PremiumCover from "../shared/PremiumCover";
import InteractiveHeart from "../shared/InteractiveHeart";
import useHoverPrefetch from "../shared/useHoverPrefetch";
import ShimmerImg from "../shared/ShimmerImg";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Mosaic template — true masonry preserving natural aspect ratios.
 * Uses react-photo-album's "rows" layout which packs photos by row height to
 * avoid the white-space holes a naive column-based masonry leaves.
 *
 * Chapters render as low-noise dividers between sub-grids so the dense feel of
 * masonry isn't broken by oversized typography.
 */
export default function MosaicTemplate({ gallery, accent, ui, actions }) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const photos = useMemo(() => gallery.photos || [], [gallery.photos]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const chapters = useMemo(
    () => (gallery.chapters || []).slice().sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
    [gallery.chapters]
  );
  const grouped = useMemo(() => groupByChapter(photos, chapters), [photos, chapters]);

  return (
    <div className="bg-stone-50 text-stone-900 min-h-screen" style={{ fontFamily: "var(--gallery-font-sans)" }} data-testid="mosaic-template">
      <PremiumCover gallery={gallery} accent={accent} selectedCount={ui.selectedCount} />

      {gallery.welcome_message && (
        <div className="max-w-2xl mx-auto px-6 pt-14 pb-6 text-center">
          <p className="text-sm sm:text-base leading-relaxed text-stone-600" style={{ fontFamily: "var(--gallery-font-serif)" }}>
            {gallery.welcome_message}
          </p>
        </div>
      )}

      {ui.phaseBanner}
      {ui.downloadBar}
      {ui.interactive?.headerStrip}

      <div className="max-w-[1500px] mx-auto px-3 sm:px-5 pb-20">
        {grouped.map((group, gi) => (
          <section key={group.key} className="mb-10">
            {group.chapter && (
              <ChapterDivider chapter={group.chapter} variant="mosaic" index={gi - (grouped[0].chapter ? 0 : 1)} />
            )}
            <MasonryGrid photos={group.photos} actions={actions} ui={ui} watermark={gallery.watermark_enabled} />
          </section>
        ))}
      </div>

      {ui.selectionBar}
      <FooterSignature gallery={gallery} variant="light" />
    </div>
  );
}

function groupByChapter(photos, chapters) {
  if (!chapters.length) return [{ key: "all", chapter: null, photos }];
  const byId = Object.fromEntries(chapters.map((c) => [c.id, []]));
  const unassigned = [];
  for (const p of photos) {
    if (p.chapter_id && byId[p.chapter_id]) byId[p.chapter_id].push(p);
    else unassigned.push(p);
  }
  const out = [];
  if (unassigned.length) out.push({ key: "intro", chapter: null, photos: unassigned });
  for (const c of chapters) {
    out.push({ key: c.id, chapter: c, photos: byId[c.id] });
  }
  return out.filter((g) => g.photos.length > 0);
}

function MasonryGrid({ photos, actions, ui, watermark }) {
  // react-photo-album expects {src, width, height}. Fall back to 3:2 if the
  // backend hasn't captured dimensions yet (legacy uploads pre-this-release).
  const albumPhotos = useMemo(
    () =>
      photos.map((p) => ({
        key: p.id,
        src: `${BACKEND}${p.url}${p.url?.includes("?") ? "&" : "?"}${ui.designPreview ? "w=1200&q=82" : "w=1000&q=80"}`,
        width: p.width || 1500,
        height: p.height || 1000,
        photo: p,
      })),
    [photos, ui.designPreview],
  );

  return (
    <RowsPhotoAlbum
      photos={albumPhotos}
      targetRowHeight={(containerWidth) => (containerWidth < 640 ? 220 : containerWidth < 1100 ? 280 : 360)}
      spacing={6}
      rowConstraints={{ singleRowMaxHeight: 480 }}
      onClick={({ photo }) => actions.openLightbox(photo.photo)}
      render={{
        photo: ({ onClick }, { photo }) => (
          <MasonryPhoto onClick={onClick} item={photo} actions={actions} ui={ui} watermark={watermark} />
        ),
      }}
    />
  );
}

function MasonryPhoto({ onClick, item, actions, ui, watermark }) {
  const photo = item.photo;
  const fav = ui.favorites?.[photo.id];
  const isFav = !!fav?.favorited;
  const hover = useHoverPrefetch(photo.url);
  return (
    <motion.div
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5 }}
      className="relative group overflow-hidden bg-stone-100 w-full h-full"
      style={{ contentVisibility: "auto", containIntrinsicSize: "500px 500px" }}
      onMouseEnter={hover.onMouseEnter}
      onMouseLeave={hover.onMouseLeave}
      data-testid={`mosaic-photo-${photo.id}`}
    >
      <button onClick={onClick} className="block w-full h-full">
        <ShimmerImg
          wrapperClassName="w-full h-full"
          src={item.src}
          alt={photo.filename}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
        />
        {watermark && (
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center text-white/15 text-2xl font-light tracking-[0.3em] uppercase rotate-[-15deg]">
            Preview
          </div>
        )}
      </button>
      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
      {ui.interactive?.enabled && (
        <div className="absolute top-2 right-2 z-10 flex items-center gap-1.5" data-testid={`int-fav-${photo.id}`}>
          {ui.interactive.renderCommentBadge?.(photo)}
          <InteractiveHeart
            isMine={ui.interactive.isMine(photo.id)}
            count={ui.interactive.getEntry(photo.id).count}
            collaborators={ui.interactive.getEntry(photo.id).collaborators}
            onToggle={() => ui.interactive.toggle(photo.id)}
            testid={`fav-btn-${photo.id}`}
          />
        </div>
      )}
      {ui.interactive?.renderLiveOverlay?.(photo)}
      {!ui.isDelivered && !ui.interactive?.enabled && (
        <button
          onClick={() => actions.toggleFav(photo.id)}
          className={`absolute top-2 right-2 w-9 h-9 inline-flex items-center justify-center backdrop-blur transition-all ${
            isFav ? "text-white opacity-100" : "bg-white/0 hover:bg-white/85 text-white opacity-0 group-hover:opacity-100"
          }`}
          style={isFav ? { backgroundColor: "var(--gallery-accent, #C9A961)" } : undefined}
          aria-label="Favorite"
          data-testid={`fav-btn-${photo.id}`}
        >
          <Heart className={`w-4 h-4 ${isFav ? "fill-white" : ""}`} />
        </button>
      )}
      {ui.isDelivered && ui.downloadsAvailable && (
        <button
          onClick={() => actions.downloadOne(photo)}
          className="absolute top-2 right-2 w-9 h-9 inline-flex items-center justify-center bg-white/0 hover:bg-white/85 text-white opacity-0 group-hover:opacity-100 backdrop-blur"
          aria-label="Download"
          data-testid={`dl-btn-${photo.id}`}
        >
          <Download className="w-4 h-4" />
        </button>
      )}
    </motion.div>
  );
}
